import axios from 'axios';

const URL_V2 = process.env.VUE_APP_BACKEND_URL_V2;
const ADMIN_URL = process.env.VUE_APP_ADMIN_URL;

/**
 * Mensaje que el backend devuelve en `{ status, message, code }` para los errores
 * de validación (ej. el tope por evento del servicio). Cuando no hay mensaje del
 * backend se usa el fallback genérico del llamador.
 */
export function refundErrorMessage(error, fallback) {
  return error?.response?.data?.message || fallback;
}

export default class RefundsServices {
  static async getRefunds(
    page,
    limit,
    states,
    search,
    orderBy = 'referenceIndex',
    orderDirection = 'desc',
    userId = null,
    supplier = null
  ) {
    try {
      const accessToken = JSON.parse(localStorage.getItem('accessToken'));
      let s = '';
      for (var s1 of states) {
        if (s1 == 0) {
          s = `${s}&register=true`;
        } else if (s1 == 1) {
          s = `${s}&review=true`;
        } else if (s1 == 2) {
          s = `${s}&modify=true`;
        } else if (s1 == 3) {
          s = `${s}&rejected=true`;
        } else if (s1 == 4) {
          s = `${s}&approved=true`;
        } else if (s1 == 5) {
          s = `${s}&paid=true`;
        } else if (s1 == 6) {
          s = `${s}&invalidPayment=true`;
        }
      }
      let url = `page=${page}&limit=${limit}${s}&orderBy=${orderBy}&orderDirection=${orderDirection}`;
      if (search) {
        url = `${url}&search=${search}`;
      }
      if (userId) {
        url = `${url}&userId=${userId}`;
      }
      if (supplier) {
        url = `${url}&supplier=${encodeURIComponent(supplier)}`;
      }
      const response = await axios.get(`${ADMIN_URL}/refunds?${url}`, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      const refunds = response.data.data;
      return { status: 'ok', data: refunds };
    } catch (err) {
      return { status: 'error' };
    }
  }

  static async getAllRefunds(page, limit, query, sort, userId, search, serviceId, petId) {
    try {
      const accessToken = JSON.parse(localStorage.getItem('accessToken'));
      let url = `${URL_V2}/refunds/all/refundrequests?page=${page}&limit=${limit}`;
      if (userId != null && userId.length != 0) {
        url = url + `&userId=${userId}`;
      }
      if (query != null && query.length != 0) {
        url = url + `&status=${query}`;
      }
      if (sort != null && sort.length != 0) {
        url = url + `&sort=${sort.join(',')}`;
      }
      if (search != null && search.length != 0) {
        url = url + `&search=${search}`;
      }
      if (serviceId != null && serviceId.length != 0) {
        url = url + `&serviceId=${serviceId}`;
      }
      if (petId != null && petId.length != 0) {
        url = url + `&serviceId=${petId}`;
      }
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      if (response.data.status === 'ok') {
        const refunds = response.data.data;
        return { status: 'ok', data: refunds };
      } else {
        return { status: 'error' };
      }
    } catch (error) {
      return { status: 'error' };
    }
  }

  static async getChangelogStatuses() {
    const accessToken = JSON.parse(localStorage.getItem('accessToken'));
    try {
      let url = `${URL_V2}/refunds/changelog/statuses`;
      // La ruta exige sesión (allowIfLoggedIn): sin este header devuelve 401.
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });

      if (response.data.status === 'ok') {
        const statuses = response.data.data;
        return { status: 'ok', data: statuses };
      } else {
        return { status: 'error' };
      }
    } catch (error) {
      console.error('Error fetching refund changelog statuses:', error);
      return { status: 'error' };
    }
  }

  static async updateChangelogStatus(data) {
    const accessToken = JSON.parse(localStorage.getItem('accessToken'));
    const url = `${URL_V2}/refunds/create/changelog`;
    // Errors are intentionally left to propagate (not swallowed into
    // {status:'error'}) so callers can read error.response.data.message —
    // e.g. the 067-clinic-multi-rut RUT/name mismatch message.
    const response = await axios.post(url, data, {
      headers: { Authorization: `Bearer ${accessToken}` }
    });
    return { status: 'ok', data: response.data };
  }

  /**
   * Suggests clinics matching a partial name, for the internal registration's
   * clinic autocomplete (067-clinic-multi-rut). Each match includes every RUT
   * on file for it.
   * @param {string} query - Partial clinic name, at least 2 characters.
   * @returns {Promise<Array<{clinicId: string, name: string, ruts: string[]}>>}
   */
  static async searchClinics(query) {
    const accessToken = JSON.parse(localStorage.getItem('accessToken'));
    try {
      const response = await axios.get(`${URL_V2}/refunds/clinics/search`, {
        params: { q: query },
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      return response.data.results || [];
    } catch (error) {
      return [];
    }
  }

  /**
   * Resolves a RUT to its known name(s), for the internal registration's
   * RUT-to-name auto-fill (072-clinic-rut-multiple-names).
   * @param {string} rut
   * @returns {Promise<{clinicId: string, name?: string, tradeName?: string, businessName?: string, defaultName?: string} | null>}
   */
  static async getClinicByRut(rut) {
    const accessToken = JSON.parse(localStorage.getItem('accessToken'));
    try {
      const response = await axios.get(`${URL_V2}/refunds/clinics/by-rut`, {
        params: { rut },
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      return response.data.clinic || null;
    } catch (error) {
      return null;
    }
  }

  static async createRefund(data) {
    const accessToken = JSON.parse(localStorage.getItem('accessToken'));
    try {
      let url = `${ADMIN_URL}/refunds/create-refund`;
      const response = await axios.post(url, data, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      if (response.status == 200) {
        return { status: 'ok', data: response.data };
      } else {
        return { status: 'error' };
      }
    } catch (error) {
      return { status: 'error' };
    }
  }
  static async getRefundDetails(refundId) {
    const accessToken = JSON.parse(localStorage.getItem('accessToken'));
    try {
      let url = `${ADMIN_URL}/refunds/${refundId}`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      return response.data.data;
    } catch (error) {
      console.error('Error fetching refund details:', error);
      throw error;
    }
  }

  /**
   * Pide los bytes de un adjunto al backend, que los reenvía desde S3.
   * Usa fetch (no axios) para poder cortar la descarga apenas llegan los
   * headers cuando el archivo no necesita conversión.
   */
  static fetchAttachmentFile(attachmentId, signal) {
    const accessToken = JSON.parse(localStorage.getItem('accessToken'));
    return fetch(`${ADMIN_URL}/refunds/attachments/${attachmentId}/file`, {
      headers: { Authorization: `Bearer ${accessToken}` },
      signal
    });
  }

  static async postRecordAndRefundDetails(body) {
    const accessToken = JSON.parse(localStorage.getItem('accessToken'));
    try {
      const url = `${ADMIN_URL}/refunds/commit/status-records`;
      const response = await axios.post(url, body, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      return response.data.data;
    } catch (error) {
      console.error('Error post details:', error);
      throw error;
    }
  }

  /**
   * Guarda el registro de revisión (fecha de uso, gasto, montos, anotaciones,
   * clínica) sin cambiar el estado ni registrar uso del servicio.
   * Los errores se propagan para que el llamador lea error.response.data.
   */
  static async saveReviewRecord(refundId, payload) {
    const accessToken = JSON.parse(localStorage.getItem('accessToken'));
    const url = `${ADMIN_URL}/refunds/${refundId}/review-record`;
    const response = await axios.patch(url, payload, {
      headers: { Authorization: `Bearer ${accessToken}` }
    });
    return response.data.data;
  }

  static async quickUpdate(refundId, payload) {
    const accessToken = JSON.parse(localStorage.getItem('accessToken'));
    try {
      const url = `${ADMIN_URL}/refunds/${refundId}/quick-update`;
      const response = await axios.patch(url, payload, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      return response.data;
    } catch (error) {
      console.error('Error quick update refund:', error);
      throw error;
    }
  }

  /**
   * Sube pagos masivos agrupados por fecha.
   * @param {{ data: Array<{ fecha: string, refunds: string[] }> }} payload
   */
  static async uploadPayments(payload) {
    const accessToken = JSON.parse(localStorage.getItem('accessToken'));
    try {
      const url = `${ADMIN_URL}/refunds/upload-payments`;
      const response = await axios.post(url, payload, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      return response.data;
    } catch (error) {
      console.error('Error uploading payments:', error);
      return { status: 'error' };
    }
  }

  /**
   * Nómina de pagos de los reembolsos aprobados (no pagados) de un país.
   * CL devuelve el cuerpo de un CSV; PE, un bloque de texto para copiar.
   */
  static async getPayroll(country) {
    const accessToken = JSON.parse(localStorage.getItem('accessToken'));
    try {
      const response = await axios.get(`${ADMIN_URL}/refunds/payroll?country=${country}`, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      return { status: 'ok', data: response.data.data };
    } catch (error) {
      const message = error?.response?.data?.error?.message || 'No se pudo generar la nómina.';
      return { status: 'error', message };
    }
  }

  /**
   * Nómina de Chile en el formato Global66 (.xlsm), lista para subir a
   * Global66 sin pasar por el CSV del banco. El backend siempre devuelve el
   * archivo binario, incluso cuando no hay reembolsos por pagar (planilla
   * vacía), así que no hay caso de "sin datos" a manejar aquí.
   */
  static async getGlobal66Payroll() {
    const accessToken = JSON.parse(localStorage.getItem('accessToken'));
    try {
      const response = await axios.get(`${ADMIN_URL}/refunds/payroll/global66`, {
        headers: { Authorization: `Bearer ${accessToken}` },
        responseType: 'blob'
      });
      const excludedCount = Number(response.headers['x-refund-payroll-excluded-count'] || 0);
      return { status: 'ok', blob: response.data, excludedCount };
    } catch (error) {
      const message = await RefundsServices.parseBlobErrorMessage(error);
      return { status: 'error', message };
    }
  }

  /**
   * Vista previa de la nómina Global66: las mismas filas que terminarían en
   * el Excel, pero como JSON, para mostrarlas en una tabla antes de descargar
   * el archivo binario.
   */
  static async getGlobal66Preview() {
    const accessToken = JSON.parse(localStorage.getItem('accessToken'));
    try {
      const response = await axios.get(`${ADMIN_URL}/refunds/payroll/global66/preview`, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      return { status: 'ok', data: response.data.data };
    } catch (error) {
      const message = error?.response?.data?.error?.message || 'No se pudo generar la vista previa.';
      return { status: 'error', message };
    }
  }

  /**
   * Cuando la request pide `responseType: 'blob'`, axios no parsea el cuerpo
   * de un error como JSON — llega como Blob aunque el backend haya
   * respondido `{ error: { message } }`. Hay que leerlo aparte.
   */
  static async parseBlobErrorMessage(error) {
    const data = error?.response?.data;
    if (!(data instanceof Blob)) {
      return error?.response?.data?.error?.message || 'No se pudo generar la nómina.';
    }
    try {
      const parsed = JSON.parse(await data.text());
      return parsed?.error?.message || 'No se pudo generar la nómina.';
    } catch (parseError) {
      return 'No se pudo generar la nómina.';
    }
  }

  static async getSuppliers() {
    const accessToken = JSON.parse(localStorage.getItem('accessToken'));
    try {
      const response = await axios.get(`${ADMIN_URL}/refunds/suppliers`, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      if (response.data.status === 'ok') {
        return { status: 'ok', data: response.data.data };
      }
      return { status: 'error' };
    } catch {
      return { status: 'error' };
    }
  }

  static async getAttachmentTypes() {
    const accessToken = JSON.parse(localStorage.getItem('accessToken'));
    try {
      const response = await axios.get(`${ADMIN_URL}/refunds/attachment-types`, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      if (response.data.status === 'ok') {
        return { status: 'ok', data: response.data.data };
      }
      return { status: 'error' };
    } catch {
      return { status: 'error' };
    }
  }

  static async checkEligibility(petId, serviceId) {
    const accessToken = JSON.parse(localStorage.getItem('accessToken'));
    try {
      const response = await axios.get(`${ADMIN_URL}/refunds/eligibility`, {
        params: { petId, serviceId },
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      if (response.data.data) {
        return { status: 'ok', data: response.data.data };
      }
      return { status: 'error' };
    } catch {
      return { status: 'error' };
    }
  }

  static async addAttachments(refundId, attachments, comments) {
    const accessToken = JSON.parse(localStorage.getItem('accessToken'));
    try {
      const body = { attachments };
      if (comments) body.comments = comments;
      const response = await axios.post(
        `${ADMIN_URL}/refunds/${refundId}/attachments`,
        body,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      if (response.data.status === 'ok') {
        return { status: 'ok', data: response.data.data };
      }
      return { status: 'error' };
    } catch {
      return { status: 'error' };
    }
  }
}
